import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PagedTileGrid from './PagedTileGrid';
import { ClimbTile } from '../climbs/ClimbTile';
import { usersService } from '../../services/users';
import { fromProfileState, type ProfileOrigin } from './profile-origin';
import type { CreatedClimb } from '../../types/send';

interface UserCreatedClimbsGridProps {
  userSlug: string;
  /** The profile the grid is on, which the climb pages it opens lead back to. */
  profile?: ProfileOrigin;
  className?: string;
}

/**
 * The climbs, routes and board problems a climber put up, as the same grid
 * of photos as their sends — newest first, ten at a time. One they deleted
 * keeps its place and its link, greyed out.
 */
export default function UserCreatedClimbsGrid({ userSlug, profile, className }: UserCreatedClimbsGridProps) {
  const { t } = useTranslation();

  const fetchPage = useCallback(async (page: number) => {
    const response = await usersService.getCreatedClimbs(userSlug, page);
    return { items: response.data, hasMore: response.meta.has_more };
  }, [userSlug]);

  return (
    <PagedTileGrid<CreatedClimb>
      fetchPage={fetchPage}
      renderTile={(created) => (
        <ClimbTile
          climb={created.attributes.climb}
          linkState={profile && fromProfileState(profile)}
          discardedLabel={created.attributes.discarded ? t('userProfile.createdClimbDeleted') : undefined}
        />
      )}
      itemKey={(created) => created.id}
      emptyText={t('climbs.noClimbsCreated')}
      errorText={t('userProfile.createdClimbsLoadError')}
      testId="user-created-climbs-grid"
      className={className}
    />
  );
}
